import type MarkdownIt from 'markdown-it'
import { ref } from 'vue'
import type { Ref } from 'vue'

export interface Toc {
  slug: string
  title: string
  level: number
  tag: string
  children?: Toc[]
}

// 使用响应式 ref 以便 Vue 能够追踪变化
const toc: Ref<Toc[]> = ref([])
const flatToc: Ref<Toc[]> = ref([])
let tocLevels: number[] = [1, 2, 3]

// 延迟加载标记
let markdownInstance: MarkdownIt | null = null
let initPromise: Promise<MarkdownIt> | null = null

// 渲染缓存（包含 HTML 和 TOC）
interface RenderCacheItem {
  html: string
  toc: Toc[]
  flatToc: Toc[]
}
const renderCache = new Map<string, RenderCacheItem>()
const MAX_CACHE_SIZE = 50

// 延迟初始化 markdown-it（只在首次使用时初始化）
function initMarkdown(): Promise<MarkdownIt> {
  if (markdownInstance) return Promise.resolve(markdownInstance)
  if (initPromise) return initPromise

  initPromise = (async () => {
    // 动态导入（代码分割）
    const [markdownit, hljs, markdownAnchor, markdownItCollapsible, mathjax3] = await Promise.all([
      import('markdown-it'),
      import('highlight.js'),
      import('markdown-it-anchor'),
      import('markdown-it-collapsible'),
      import('markdown-it-mathjax3')
    ])

    // highlight.js 没有 default 导出，直接使用命名空间
    // const hljs = hljsModule.default || hljsModule

    const md = markdownit
      .default({
        html: true,
        xhtmlOut: false,
        linkify: true,
        langPrefix: 'language-',
        typographer: true,
        quotes: '""\'\'',
        breaks: true,
        highlight: function (str: string, lang: string): string {
          const className = `language-${lang}`
          if (lang && hljs.default.getLanguage(lang)) {
            try {
              return (
                '<pre class=' +
                className +
                '><code class="hljs">' +
                hljs.default.highlight(str, { language: lang, ignoreIllegals: true }).value +
                '</code>' +
                '<small class="hljs-tag">' +
                lang +
                '</small>' +
                '</pre>'
              )
            } catch (error) {
              console.warn(`Failed to highlight code for language: ${lang}`)
            }
          }
          return `<pre><code class="${className}">${markdownit.default().utils.escapeHtml(str)}</code></pre>`
        }
      })
      .use(markdownAnchor.default, {
        level: [1, 2, 3],
        permalink: markdownAnchor.default.permalink.headerLink(),
        slugify: (text: string) => {
          return text
            .toLowerCase()
            .replace(/\[fold\]/g, '')
            .replace(/\[fold default=open\]/g, '')
            .replace(/[/!@$%^&*()+=<>#?:"{}|,.;'`~[\]]/g, '')
            .trim()
            .replace(/^(\d)/g, '_' + '$1')
            .replace(/\s+/g, '-')
        },
        callback: (token, { slug, title }) => {
          const level = parseInt(token.tag.slice(1))
          const tag = token.tag
          const item: Toc = {
            slug,
            title: title
              .trim()
              .replace(/\[fold\]/g, '')
              .replace(/\[fold default=open\]/g, ''),
            level,
            tag,
            children: []
          }
          const index = tocLevels.indexOf(level)
          try {
            if (tocLevels.includes(level) && index === 0) {
              toc.value.push(item)
              flatToc.value.push(item)
            } else if (tocLevels.includes(level)) {
              let parent = toc.value[toc.value.length - 1]
              if (parent) {
                for (let i = tocLevels[1]; i < level; i++) {
                  parent = parent.children![parent.children!.length - 1]
                }
                if (parent && parent.children) {
                  parent.children.push(item)
                  flatToc.value.push(item)
                }
              }
            }
          } catch (e) {
            console.error('TOC generation error:', e)
          }
        }
      })
      .use(mathjax3.default)
      .use(markdownItCollapsible.default)

    markdownInstance = md
    return md
  })()

  return initPromise
}

export default defineNuxtPlugin((_nuxtApp) => {
  return {
    provide: {
      markdown: {
        // 异步渲染方法（推荐使用）
        async renderAsync(content: string, levels: number[] = [1, 2, 3]): Promise<string> {
          const md = await initMarkdown()

          const cacheKey = `${content.substring(0, 100)}-${levels.join(',')}`
          if (renderCache.has(cacheKey)) {
            const cached = renderCache.get(cacheKey)!
            // 恢复缓存的 TOC 数据
            toc.value = cached.toc
            flatToc.value = cached.flatToc
            return cached.html
          }

          toc.value = []
          flatToc.value = []
          tocLevels = levels

          const result = md.render(content)

          // 限制缓存大小
          if (renderCache.size >= MAX_CACHE_SIZE) {
            const firstKey = renderCache.keys().next().value
            if (firstKey) renderCache.delete(firstKey)
          }
          // 缓存 HTML 和 TOC 数据
          renderCache.set(cacheKey, {
            html: result,
            toc: JSON.parse(JSON.stringify(toc.value)),
            flatToc: JSON.parse(JSON.stringify(flatToc.value))
          })

          return result
        },

        // 同步渲染方法（向后兼容，但会在第一次调用时警告）
        render(content: string, levels: number[] = [1, 2, 3]): string {
          if (!markdownInstance) {
            console.warn('⚠️ Markdown not initialized yet. Please use renderAsync() or call init() first.')
            // 触发异步初始化
            initMarkdown()
            return ''
          }

          const cacheKey = `${content.substring(0, 100)}-${levels.join(',')}`
          if (renderCache.has(cacheKey)) {
            const cached = renderCache.get(cacheKey)!
            // 恢复缓存的 TOC 数据
            toc.value = cached.toc
            flatToc.value = cached.flatToc
            return cached.html
          }

          toc.value = []
          flatToc.value = []
          tocLevels = levels

          const result = markdownInstance.render(content)

          if (renderCache.size >= MAX_CACHE_SIZE) {
            const firstKey = renderCache.keys().next().value
            if (firstKey) renderCache.delete(firstKey)
          }
          // 缓存 HTML 和 TOC 数据
          renderCache.set(cacheKey, {
            html: result,
            toc: JSON.parse(JSON.stringify(toc.value)),
            flatToc: JSON.parse(JSON.stringify(flatToc.value))
          })

          return result
        },

        // 预初始化方法
        init: initMarkdown,

        // 清空缓存
        clearCache() {
          renderCache.clear()
        }
      },
      toc,
      flatToc
    },
    parallel: true
  }
})
